/**
 * Base Service
 * Standardiserade API-anrop med konsekvent respons-utpackning och felhantering.
 * Alla domänspecifika services bör använda dessa hjälpfunktioner.
 *
 * Exempel:
 *   import { apiGet, apiPost } from '@/services/api/baseService';
 *   const customers = await apiGet<Customer[]>('/api/crm/customers');
 *   const created  = await apiPost<Customer>('/api/crm/customers', body);
 */

import api from './client';
import { AxiosRequestConfig } from 'axios';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * GET med automatisk `.data.data`-utpackning.
 */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data;
}

/**
 * GET med paginering — returnerar `{ data, pagination }`.
 */
export async function apiGetPaginated<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<{ data: T[]; pagination: PaginatedResponse<T>['pagination'] }> {
  const response = await api.get<PaginatedResponse<T>>(url, config);
  return { data: response.data.data, pagination: response.data.pagination };
}

/**
 * POST med automatisk `.data.data`-utpackning.
 */
export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, body, config);
  return response.data.data;
}

/**
 * PUT med automatisk `.data.data`-utpackning.
 */
export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.put<ApiResponse<T>>(url, body, config);
  return response.data.data;
}

/**
 * PATCH med automatisk `.data.data`-utpackning.
 */
export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, body, config);
  return response.data.data;
}

/**
 * DELETE — returnerar void som standard.
 */
export async function apiDelete(url: string, config?: AxiosRequestConfig): Promise<void> {
  await api.delete(url, config);
}

/**
 * DELETE med respons-utpackning (om servern returnerar data).
 */
export async function apiDeleteWithResponse<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.delete<ApiResponse<T>>(url, config);
  return response.data.data;
}
